/**
 * studio.jsx — Rammy Studio (long-form editor).
 *
 * A workspace for composing multi-paragraph audio projects:
 *   • Write it yourself, paste an essay and let Rammy split it, or brief
 *     Rammy and let it draft the script.
 *   • Per paragraph: voice, speed, pause after, and a one-click preview so
 *     picking a voice is never guesswork.
 *   • Project-wide pronunciation rules, ambience (Rammy's own loops or a
 *     described one), cover art, and video export shape.
 *   • Every render is kept in Creations — nothing dies with the tab.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import api from "@/lib/api";
import { mixFullTrack, speechSegments, buildDuckEnvelope } from "@/lib/studio-mix";
import { renderAudiogram } from "@/lib/studio-audiogram";
import { previewLine, stopPreview } from "@/lib/studio-preview";
import { AudioTimeline } from "@/components/studio/AudioTimeline";
import { ScriptTools } from "@/components/studio/ScriptTools";
import { PronunciationEditor } from "@/components/studio/PronunciationEditor";
import { AmbienceEditor } from "@/components/studio/AmbienceEditor";
import { ExportOptions } from "@/components/studio/ExportOptions";
import { MusicBed } from "@/components/studio/MusicBed";
import { BrandKitPanel } from "@/components/studio/BrandKitPanel";
import { DubDialog } from "@/components/studio/DubDialog";
import { SceneVisual } from "@/components/studio/SceneVisual";
import { StudioModes, MODES } from "@/components/studio/StudioModes";
import BriefStudio from "@/components/studio/BriefStudio";
import { ClipStudio } from "@/components/studio/ClipStudio";
import * as L from "lucide-react";

const MAX_BLOCKS = 20;
const MAX_TOTAL_CHARS = 5000;

/** m:ss — the only duration format anyone reads at a glance. */
const fmtClock = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const MAX_BLOCK_CHARS = 1000;
const DEFAULT_EXPORT = { aspect: "1:1", caption_style: "classic" };

// Default voice roster — kept in sync with backend `ELEVEN_VOICE_MAP` so the
// picker has labels before /catalog/voices resolves.
const DEFAULT_VOICE_ROSTER = [
  { id: "hale",  name: "Hale"  },
  { id: "amber", name: "Amber" },
  { id: "iris",  name: "Iris"  },
  { id: "milo",  name: "Milo"  },
  { id: "sage",  name: "Sage"  },
  { id: "nova",  name: "Nova"  },
];

const STUDIO_TEMPLATES = [
  {
    id: "podcast",
    icon: "Mic",
    title: "Podcast episode",
    tag: "Two-voice",
    tagline: "Host & guest conversation with warm outro.",
    ambience: { source: "library", slug: "cafe", prompt: null, volume: 0.14 },
    titlePreset: "Untitled episode",
    blocks: [
      { text: "Welcome to the show. I'm your host, and today we're talking about the small habits that shape a life.", voice: "hale" },
      { text: "Thanks for having me. I think most people underestimate how much the first thirty minutes of a morning sets the tone for everything else.", voice: "amber" },
      { text: "That's beautifully put. Let's dive in.", voice: "hale" },
    ],
  },
  {
    id: "audiobook",
    icon: "BookOpen",
    title: "Audiobook chapter",
    tag: "Narrator",
    tagline: "Single voice, longer paragraphs, cinematic.",
    ambience: null,
    titlePreset: "Chapter one",
    blocks: [
      { text: "The city was still asleep when she stepped out onto the balcony. A single pigeon coughed from a fire escape somewhere below.", voice: "iris" },
      { text: "She had not slept — not really — and the coffee in her hands was already going cold. That was fine. Cold coffee was still coffee.", voice: "iris" },
    ],
  },
  {
    id: "meditation",
    icon: "Sparkles",
    title: "Meditation script",
    tag: "Calm",
    tagline: "Soft pacing with rain underneath.",
    ambience: { source: "library", slug: "rain", prompt: null, volume: 0.22 },
    titlePreset: "Evening wind-down",
    blocks: [
      { text: "Find a comfortable position, and let your shoulders drop away from your ears.", voice: "sage", pause_ms: 900 },
      { text: "Take a slow breath in, and a slower breath out.", voice: "sage", pause_ms: 1200 },
      { text: "There is nowhere else you need to be right now.", voice: "sage", pause_ms: 900 },
    ],
  },
];

const normaliseBlock = (b) => ({
  text: b.text || "",
  voice: b.voice || "hale",
  pause_ms: Number.isFinite(b.pause_ms) ? b.pause_ms : 400,
  speed: Number.isFinite(b.speed) ? b.speed : 1.0,
  chapter: !!b.chapter,
  chapter_title: b.chapter_title || null,
  visual: b.visual?.url ? b.visual : null,
});

export function StudioScreen({ user }) {
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [customVoices, setCustomVoices] = useState([]);
  const [brand, setBrand] = useState(null);
  const [catalogVoices, setCatalogVoices] = useState(DEFAULT_VOICE_ROSTER);
  const [clipOpen, setClipOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);

  const loadProjects = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/studio/projects");
      setProjects(data.projects || []);
      // Deliberately NOT auto-opening the first project. Studio used to land
      // everyone in a paragraph form with no explanation of what the screen
      // was for; the mode chooser is the front door now.
    } catch (_) {
      toast.error("Couldn't load your Studio projects");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadProjects();
    api.get("/voice/clones").then(r => setCustomVoices(r.data?.voices || [])).catch(()=>{});
    api.get("/studio/brand-kit").then(r => setBrand(r.data)).catch(()=>{});
    api.get("/catalog/voices").then(r => {
      const v = r.data;
      if (Array.isArray(v) && v.length) setCatalogVoices(v);
    }).catch(()=>{});
  }, []);

  // Creations / chat can ask Studio to open a specific project. The intent is
  // parked in sessionStorage because the request usually arrives before this
  // screen mounts; the event covers the already-mounted case.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem("rammy-studio-open");
      if (pending) {
        sessionStorage.removeItem("rammy-studio-open");
        setActiveId(pending);
      }
    } catch (_) { /* ignore */ }
    const onOpen = (e) => { if (e.detail) setActiveId(e.detail); };
    window.addEventListener("rammy-open-studio-project", onOpen);
    return () => window.removeEventListener("rammy-open-studio-project", onOpen);
  }, []);

  const voiceOptions = useMemo(() => {
    const named = catalogVoices.map(v => ({ id: v.id, label: v.name, kind: "named" }));
    const cloned = customVoices.map(v => ({ id: v.id, label: v.name, kind: "cloned" }));
    return [...named, ...cloned];
  }, [catalogVoices, customVoices]);

  const activeProject = projects.find(p => p.id === activeId) || null;

  const createNew = async (mode = "audio") => {
    // "clip" isn't a project — it's a single shot, handled by its own surface.
    if (mode === "clip") { setBriefOpen(false); setClipOpen(true); return; }
    // Neither is a brief: Rammy decides what the assets are, then makes them.
    if (mode === "brief") { setClipOpen(false); setBriefOpen(true); return; }
    const preset = {
      audio: "Untitled narration",
      video: "Untitled video",
      photo: "Untitled photo video",
    }[mode] || "Untitled project";
    try {
      const { data } = await api.post("/studio/projects", {
        title: preset,
        mode,
        blocks: [{ text: "", voice: "hale" }],
        ambience: null,
        export: mode === "audio" ? undefined : { aspect: "9:16", caption_style: "classic" },
      });
      setProjects(prev => [data, ...prev]);
      setActiveId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create project");
    }
  };

  const addProject = (proj) => {
    setProjects(prev => [proj, ...prev.filter(p => p.id !== proj.id)]);
    setActiveId(proj.id);
  };

  const updateActive = (patch) => {
    setProjects(prev => prev.map(p => p.id === activeId ? { ...p, ...patch } : p));
  };

  const duplicateProject = async (pid) => {
    try {
      const { data } = await api.post(`/studio/projects/${pid}/duplicate`);
      setProjects(prev => [data, ...prev]);
      setActiveId(data.id);
      toast.success("Duplicated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't duplicate");
    }
  };

  const removeProject = async (pid) => {
    if (!confirm("Delete this Studio project?")) return;
    try {
      await api.delete(`/studio/projects/${pid}`);
      const rest = projects.filter(p => p.id !== pid);
      setProjects(rest);
      if (activeId === pid) setActiveId(rest[0]?.id || null);
      toast.success("Project deleted");
    } catch (_) {
      toast.error("Couldn't delete project");
    }
  };

  return (
    <div className="flex-1 min-h-0 flex" data-testid="screen-studio">
      {/* Project list rail */}
      <div className="w-[280px] shrink-0 border-r border-[var(--rammy-border)] bg-[var(--rm-surface)]/40 flex flex-col">
        <div className="h-12 border-b border-[var(--rammy-border)] px-4 flex items-center justify-between">
          <button
            onClick={() => { setActiveId(null); setClipOpen(false); setBriefOpen(false); }}
            className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] transition-colors inline-flex items-center gap-1.5"
            data-testid="studio-home"
            title="Back to the Studio menu"
          >
            <L.LayoutGrid size={12}/> Make something
          </button>
          <button
            onClick={() => { setActiveId(null); setClipOpen(false); setBriefOpen(false); }}
            className="text-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-text)] transition-colors"
            data-testid="studio-new-project"
            aria-label="New project"
            title="New project"
          >
            <L.Plus size={16}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto subtle-scroll">
          {loadingList && (
            <div className="p-4 text-sm text-[var(--rammy-text-muted)]">Loading…</div>
          )}
          {!loadingList && projects.length === 0 && (
            <div className="p-5 text-sm text-[var(--rammy-text-sec)] leading-relaxed">
              <div className="font-medium text-[var(--rammy-text)] mb-1">Nothing here yet</div>
              <div className="text-[12px]">Pick what you want to make and it'll show up here.</div>
            </div>
          )}
          {projects.map(p => {
            const mode = MODES.find(m => m.id === (p.mode || "audio"));
            const ModeIcon = mode?.icon || L.AudioLines;
            return (
              <button
                key={p.id}
                onClick={() => { setActiveId(p.id); setClipOpen(false); setBriefOpen(false); }}
                data-testid={`studio-project-${p.id}`}
                data-active={activeId === p.id ? "true" : "false"}
                className={`w-full text-left px-4 py-3 border-b border-[var(--rammy-border)]/60 transition-colors ${
                  activeId === p.id
                    ? "bg-[var(--rammy-cyan)]/15"
                    : "hover:bg-[var(--rammy-surface-alt)]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <ModeIcon size={13} className="text-[var(--rammy-cyan-dark)] shrink-0"/>
                  <div className="text-sm font-medium truncate">{p.title || "Untitled"}</div>
                </div>
                <div className="text-[11px] text-[var(--rammy-text-muted)] mt-0.5 pl-[21px]">
                  {(p.blocks?.length || 0)} scene{(p.blocks?.length || 0) === 1 ? "" : "s"}
                  {p.last_generated_at
                    ? ` · ${fmtClock(p.last_duration_seconds || 0)}`
                    : " · not made yet"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 min-w-0 overflow-y-auto subtle-scroll">
        {briefOpen ? (
          <BriefStudio onBack={() => setBriefOpen(false)} />
        ) : clipOpen ? (
          <ClipStudio onBack={() => setClipOpen(false)} />
        ) : activeProject ? (
          <StudioEditor
            key={activeProject.id}
            project={activeProject}
            voiceOptions={voiceOptions}
            brand={brand}
            onBrandChange={setBrand}
            onProjectAdded={addProject}
            onProjectChange={updateActive}
            onDelete={() => removeProject(activeProject.id)}
            onDuplicate={() => duplicateProject(activeProject.id)}
            onSaved={loadProjects}
          />
        ) : (
          !loadingList && (
            <StudioModes
              onPick={createNew}
              projectCount={projects.length}
              onBrowse={() => setActiveId(projects[0]?.id || null)}
            />
          )
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editor pane                                                          */
/* ------------------------------------------------------------------ */
function StudioEditor({ project, voiceOptions, brand, onBrandChange, onProjectAdded, onProjectChange, onDelete, onDuplicate, onSaved }) {
  const [title, setTitle] = useState(project.title || "");
  const [blocks, setBlocks] = useState(
    project.blocks?.length ? project.blocks.map(normaliseBlock) : [normaliseBlock({})]
  );
  const [ambience, setAmbience] = useState(project.ambience || null);
  const [music, setMusic] = useState(project.music || null);
  const [pronunciations, setPronunciations] = useState(project.pronunciations || []);
  const [exportSettings, setExportSettings] = useState(project.export || DEFAULT_EXPORT);
  const [coverDataUrl, setCoverDataUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [render, setRender] = useState(null);
  const [downloadMode, setDownloadMode] = useState("merged");
  const [audiogram, setAudiogram] = useState({ busy: false, pct: 0, phase: "" });
  const [history, setHistory] = useState([]);
  const [auditionIdx, setAuditionIdx] = useState(null);
  const [savedToLibrary, setSavedToLibrary] = useState(false);
  const [plan, setPlan] = useState(null);
  const [duckEnvelope, setDuckEnvelope] = useState(null);
  const [tighten, setTighten] = useState(project.tighten_pacing !== false);
  const [dubOpen, setDubOpen] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [castList, setCastList] = useState([]);

  // What the user set out to make. Everything below reads from this instead
  // of showing every control to everybody.
  const mode = project.mode || "audio";
  const isAudio = mode === "audio";
  const isVideoish = mode === "video" || mode === "photo";
  const modeMeta = MODES.find(m => m.id === mode);

  // Saved subjects, so a scene can reuse the same face or product.
  useEffect(() => {
    api.get("/cast").then(r => setCastList(r.data?.cast || [])).catch(() => {});
  }, []);

  // Reset when project changes
  useEffect(() => {
    setTitle(project.title || "");
    setBlocks(project.blocks?.length ? project.blocks.map(normaliseBlock) : [normaliseBlock({})]);
    setAmbience(project.ambience || null);
    setMusic(project.music || null);
    setPronunciations(project.pronunciations || []);
    setExportSettings(project.export || DEFAULT_EXPORT);
    setTighten(project.tighten_pacing !== false);
    setCoverDataUrl(null);
    setRender(null);
    setHistory([]);
    setSavedToLibrary(false);
    setPlan(null);
    setDuckEnvelope(null);
    stopPreview();
  }, [project.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const totalChars = blocks.reduce((s, b) => s + (b.text?.length || 0), 0);
  const overBudget = totalChars > MAX_TOTAL_CHARS;
  // Nobody writing audio thinks in characters. ~2.6 chars a second is close
  // enough for a spoken estimate at natural pace.
  const estSeconds = Math.round(totalChars / 2.6);
  const sceneCount = blocks.filter(b => b.visual?.url).length;

  /** Snapshot the whole editable state so any edit can be undone. */
  const snapshot = useCallback(() => {
    setHistory(prev => [
      ...prev.slice(-24),
      { title, blocks, ambience, music, pronunciations, exportSettings, tighten },
    ]);
  }, [title, blocks, ambience, music, pronunciations, exportSettings, tighten]);

  const undo = useCallback(() => {
    setHistory(prev => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      setTitle(last.title);
      setBlocks(last.blocks);
      setAmbience(last.ambience);
      setMusic(last.music ?? null);
      setPronunciations(last.pronunciations);
      setExportSettings(last.exportSettings);
      if (typeof last.tighten === "boolean") setTighten(last.tighten);
      return prev.slice(0, -1);
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (tag === "textarea" || tag === "input") return;   // let native undo win
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  // The ducking envelope is derived from where speech actually is, so playback
  // in the timeline matches the exported file exactly.
  useEffect(() => {
    let dead = false;
    if (!render?.music?.url || (render.music.volume ?? 0) <= 0) {
      setDuckEnvelope(null);
      return;
    }
    (async () => {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      try {
        const segments = await speechSegments(ctx, render.stems || [], render.gaps || []);
        if (dead) return;
        setDuckEnvelope(buildDuckEnvelope({
          total: render.duration_seconds || 1,
          segments,
          full: render.music.volume,
          duck: render.music.duck ?? 0.7,
        }));
      } catch (_) {
        if (!dead) setDuckEnvelope(null);
      } finally {
        ctx.close().catch(() => {});
      }
    })();
    return () => { dead = true; };
  }, [render]);

  // Ask the server which paragraphs are still cached, so the render button can
  // promise exactly what it will do. Debounced — this runs while you type.
  useEffect(() => {
    const payload = {
      blocks: blocks.map(b => ({ text: b.text || "", voice: b.voice, pause_ms: b.pause_ms, speed: b.speed })),
      pronunciations,
    };
    const timer = setTimeout(() => {
      api.post(`/studio/projects/${project.id}/plan`, payload)
        .then(r => setPlan(r.data))
        .catch(() => setPlan(null));
    }, 700);
    return () => clearTimeout(timer);
  }, [blocks, pronunciations, project.id]);

  const setBlock = (idx, patch) => {
    setBlocks(prev => prev.map((b, i) => i === idx ? { ...b, ...patch } : b));
  };
  const addBlock = () => {
    if (blocks.length >= MAX_BLOCKS) {
      toast.error(`Max ${MAX_BLOCKS} paragraphs per project`);
      return;
    }
    snapshot();
    const prevBlock = blocks[blocks.length - 1];
    setBlocks(prev => [...prev, normaliseBlock({ voice: prevBlock?.voice, speed: prevBlock?.speed, pause_ms: prevBlock?.pause_ms })]);
  };
  const removeBlock = (idx) => {
    if (blocks.length <= 1) return;
    snapshot();
    setBlocks(prev => prev.filter((_, i) => i !== idx));
  };
  const moveBlock = (from, to) => {
    if (to < 0 || to >= blocks.length || from === to) return;
    snapshot();
    setBlocks(prev => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const chapterCount = blocks.filter(b => b.chapter).length;

  const suggestChapters = async () => {
    if (suggesting) return;
    if (blocks.filter(b => (b.text || "").trim()).length < 2) {
      toast.error("Write a couple more paragraphs first");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post(`/studio/projects/${project.id}/suggest-chapters`, {
        blocks: blocks.map(b => ({ text: b.text || "", voice: b.voice })),
      }, { timeout: 120000 });
      const byIndex = new Map((data.chapters || []).map(c => [c.index, c.title]));
      snapshot();
      setBlocks(prev => prev.map((b, i) => byIndex.has(i)
        ? { ...b, chapter: true, chapter_title: byIndex.get(i) }
        : { ...b, chapter: false, chapter_title: null }));
      toast.success(`Rammy marked ${byIndex.size} chapter${byIndex.size === 1 ? "" : "s"} — edit or clear any of them`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't suggest chapters");
    } finally {
      setSuggesting(false);
    }
  };

  const copyTimestamps = async () => {
    const chapters = render?.chapters || [];
    if (!chapters.length) return;
    const fmt = (s) => {
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${m}:${String(sec).padStart(2, "0")}`;
    };
    const text = chapters.map(c => `${fmt(c.start_seconds)} ${c.title}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Timestamps copied — paste them into your description");
    } catch (_) {
      toast.error("Couldn't copy — select them from the chapter list instead");
    }
  };

  const applyImportedBlocks = (incoming, { replace }) => {
    snapshot();
    const clean = incoming.map(normaliseBlock);
    setBlocks(prev => {
      const base = replace ? [] : prev.filter(b => b.text.trim());
      return [...base, ...clean].slice(0, MAX_BLOCKS);
    });
  };

  const saveProject = async ({ silent = false } = {}) => {
    if (overBudget) {
      toast.error(`Trim your script — ${totalChars}/${MAX_TOTAL_CHARS} characters.`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/studio/projects/${project.id}`, {
        title: (title || "").trim() || "Untitled project",
        mode,
        blocks: blocks.map(normaliseBlock),
        ambience: ambience ? {
          source: ambience.source || "custom",
          slug: ambience.slug || null,
          prompt: ambience.prompt || null,
          volume: ambience.volume ?? 0.18,
        } : null,
        music: music ? {
          source: music.source || "library",
          slug: music.slug || null,
          track_id: music.track_id || null,
          volume: music.volume ?? 0.22,
          duck: music.duck ?? 0.7,
        } : null,
        pronunciations,
        export: exportSettings,
        tighten_pacing: tighten,
      });
      onProjectChange({
        title: data.title, blocks: data.blocks, ambience: data.ambience, music: data.music,
        pronunciations: data.pronunciations, export: data.export,
        tighten_pacing: data.tighten_pacing, updated_at: data.updated_at,
      });
      if (!silent) toast.success("Project saved");
      onSaved?.();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    if (!blocks.some(b => (b.text || "").trim())) {
      toast.error("Add at least one paragraph of text first");
      return;
    }
    const ok = await saveProject({ silent: true });
    if (!ok || overBudget) return;

    setGenerating(true);
    setRender(null);
    setSavedToLibrary(false);
    toast.info("Rendering — this can take up to a minute for long scripts.");
    try {
      const { data } = await api.post(`/studio/projects/${project.id}/generate`, {}, {
        timeout: 300000,
      });
      setRender(data);
      setSavedToLibrary(!!data.creation_id);
      const reusedCount = (data.reused || []).filter(Boolean).length;
      const madeCount = data.rendered_count ?? (data.stems || []).length;
      toast.success(
        reusedCount
          ? `Re-voiced ${madeCount} paragraph${madeCount === 1 ? "" : "s"}, reused ${reusedCount}` +
            (data.creation_id ? " — saved to Creations" : "")
          : (data.creation_id
            ? `Rendered ${Math.round(data.duration_seconds || 0)}s — saved to Creations`
            : `Rendered ${Math.round(data.duration_seconds || 0)}s of audio`)
      );
      setPlan(p => p ? { ...p, blocks: p.blocks.map(b => ({ ...b, cached: true })), to_render: 0 } : p);
      onProjectChange({
        last_generated_at: data.generated_at,
        last_duration_seconds: data.duration_seconds,
      });
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const downloadBlob = (b64, mime, filename) => {
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const safeName = () => (title || "project").replace(/[^a-z0-9-_ ]/gi, "").trim() || "rammy-studio";

  /** Music is served by URL (an uploaded track can be 20 MB) — fetch the bytes
   *  once and reuse them for the audio mix and the video. */
  const fetchMusicBuffer = async () => {
    const m = render?.music;
    if (!m?.url || (m.volume ?? 0) <= 0) return null;
    try {
      const path = m.url.replace(/^\/api/, "");
      const res = await api.get(path, { responseType: "arraybuffer", timeout: 180000 });
      return res.data;
    } catch (e) {
      console.warn("music fetch failed", e);
      toast.error("Couldn't fetch the music bed — continuing without it.");
      return null;
    }
  };

  const downloadMerged = async () => {
    if (!render?.merged_mp3_b64) return;
    const hasAmbience = !!render.ambience_mp3_b64 && (render.ambience_volume ?? 0) > 0;
    const hasMusic = !!render.music?.url && (render.music?.volume ?? 0) > 0;
    if (!hasAmbience && !hasMusic) {
      downloadBlob(render.merged_mp3_b64, "audio/mpeg", `${safeName()}.mp3`);
      return;
    }
    toast.info(hasMusic ? "Mixing music and ducking it under the voice…" : "Mixing ambience under narration…");
    try {
      const musicBuffer = hasMusic ? await fetchMusicBuffer() : null;
      const blob = await mixFullTrack({
        narrationB64: render.merged_mp3_b64,
        ambienceB64: hasAmbience ? render.ambience_mp3_b64 : null,
        ambienceVolume: render.ambience_volume,
        musicBuffer,
        musicVolume: render.music?.volume ?? 0,
        musicDuck: render.music?.duck ?? 0.7,
        stems: render.stems || [],
        gaps: render.gaps || [],
      });
      if (!blob) throw new Error("Mix failed");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName()}.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (_) {
      toast.error("Couldn't mix — falling back to narration only.");
      downloadBlob(render.merged_mp3_b64, "audio/mpeg", `${safeName()}.mp3`);
    }
  };

  const downloadStem = (idx) => {
    const stem = render?.stems?.[idx];
    if (!stem) return;
    downloadBlob(stem.audio_b64, stem.mime, `${safeName()}-p${idx + 1}-${stem.voice}.mp3`);
  };
  const downloadAllStems = () => {
    (render?.stems || []).forEach((_, i) => setTimeout(() => downloadStem(i), i * 200));
  };
  const playStem = (idx) => {
    const stem = render?.stems?.[idx];
    if (!stem) return;
    stopPreview();
    new Audio(`data:${stem.mime};base64,${stem.audio_b64}`).play().catch(() => toast.error("Couldn't play preview"));
  };

  const [assembly, setAssembly] = useState(null);   // server-side MP4 job
  const assembleTimer = useRef(null);
  const assembling = assembly?.status === "queued" || assembly?.status === "voicing"
    || assembly?.status === "rendering";

  useEffect(() => () => clearInterval(assembleTimer.current), []);

  /** Make the real video: scenes, the voiceover and burned-in captions, cut
   *  server-side into one MP4. The audiogram below stays for the waveform
   *  look — this is the one that uses the pictures you chose. */
  const assembleVideo = async () => {
    if (!render || assembly?.status === "voicing" || assembly?.status === "rendering") return;
    try {
      const { data } = await api.post(`/studio/projects/${project.id}/assemble`, {
        aspect: exportSettings?.aspect || "9:16",
        captions: true,
      });
      setAssembly(data);
      clearInterval(assembleTimer.current);
      assembleTimer.current = setInterval(async () => {
        try {
          const r = await api.get(`/studio/assemble/${data.job_id}`);
          setAssembly(r.data);
          if (r.data.status === "done" || r.data.status === "error") {
            clearInterval(assembleTimer.current);
            if (r.data.status === "done") toast.success("Your video's ready — it's in Creations too");
            else toast.error(r.data.error || "Couldn't make that video");
          }
        } catch (_) { /* a blip shouldn't kill the poll */ }
      }, 4000);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Couldn't start that render");
    }
  };

  /** Record the audiogram, transcode to MP4, download it, and keep a copy in
   *  the Creations library. */
  const exportAudiogram = async () => {
    if (!render || audiogram.busy) return;
    setAudiogram({ busy: true, pct: 0, phase: "recording" });
    let logoObjectUrl = null;
    try {
      const musicBuffer = await fetchMusicBuffer();
      if (brand?.has_logo) {
        try {
          const res = await api.get("/studio/brand-kit/logo", { responseType: "blob" });
          logoObjectUrl = URL.createObjectURL(res.data);
        } catch (_) { /* export without the logo rather than not at all */ }
      }
      const webmBlob = await renderAudiogram({
        narrationB64: render.merged_mp3_b64,
        ambienceB64: render.ambience_mp3_b64 || null,
        ambienceVolume: render.ambience_volume ?? 0.18,
        stems: render.stems || [],
        blocks,
        gaps: render.gaps || [],
        title: title || "Rammy Studio",
        coverDataUrl,
        aspect: exportSettings?.aspect || "1:1",
        captionStyle: exportSettings?.caption_style || "classic",
        musicBuffer,
        musicVolume: render.music?.volume ?? 0,
        musicDuck: render.music?.duck ?? 0.7,
        brand: brand || {},
        logoUrl: logoObjectUrl,
        onProgress: (pct) => setAudiogram({ busy: true, pct: pct * 0.6, phase: "recording" }),
      });

      const { webmToMp4 } = await import("@/lib/studio-mp4");
      setAudiogram({ busy: true, pct: 0.62, phase: "converting" });
      const mp4Blob = await webmToMp4(webmBlob, {
        onProgress: (p) => setAudiogram({ busy: true, pct: 0.62 + 0.36 * p, phase: "converting" }),
      });

      const url = URL.createObjectURL(mp4Blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const slug = (title || "audiogram").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "audiogram";
      a.download = `${slug}-${stamp}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);

      // Keep it — a video you can only find in Downloads isn't kept.
      try {
        const form = new FormData();
        form.append("file", mp4Blob, `${slug}.mp4`);
        form.append("title", title || "Untitled");
        form.append("project_id", project.id);
        form.append("kind", "video");
        form.append("duration_seconds", String(render.duration_seconds || 0));
        await api.post("/studio/creations", form, { timeout: 300000 });
        toast.success("Video downloaded and saved to Creations");
      } catch (_) {
        toast.success("Audiogram MP4 downloaded");
      }
    } catch (e) {
      console.warn("audiogram export failed:", e);
      toast.error("Couldn't render the video — try again in a moment.");
    } finally {
      if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);
      setAudiogram({ busy: false, pct: 0, phase: "" });
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5">
      {dubOpen && (
        <DubDialog
          projectId={project.id}
          onClose={() => setDubOpen(false)}
          onCreated={(proj) => { setDubOpen(false); onProjectAdded?.(proj); onSaved?.(); }}
        />
      )}
      {/* Title + actions */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-1">Project title</div>
          <div className="flex items-center gap-2" data-testid="studio-mode-badge">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] text-[10px] uppercase tracking-[0.16em] font-semibold">
              {modeMeta?.kicker || "Text → Audio"}
            </span>
          </div>
          <Input
            value={title}
            onChange={(e)=>setTitle(e.target.value)}
            placeholder="Give your project a name"
            className="rounded-2xl h-12 text-xl font-display border-transparent focus-visible:border-[var(--rammy-cyan)] shadow-none bg-transparent px-0"
            maxLength={120}
            data-testid="studio-title-input"
          />
          <div className="text-xs text-[var(--rammy-text-muted)] mt-1">
            {blocks.length} {mode === "photo" ? "photo" : mode === "video" ? "scene" : "paragraph"}{blocks.length === 1 ? "" : "s"} · about {fmtClock(estSeconds)}
            {isVideoish && ` · ${sceneCount} of ${blocks.length} with a visual`}
            {overBudget && (
              <span className="text-red-500"> · {totalChars.toLocaleString()}/{MAX_TOTAL_CHARS.toLocaleString()} characters, too long</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 pt-6">
          <Button
            variant="ghost"
            onClick={undo}
            disabled={!history.length}
            className="btn-pill h-9 text-xs disabled:opacity-40"
            data-testid="studio-undo"
            title="Undo (⌘Z)"
          >
            <L.Undo2 size={14}/> Undo
          </Button>
          <Button
            variant="outline"
            onClick={() => saveProject()}
            disabled={saving || overBudget}
            className="btn-pill h-9 text-xs"
            data-testid="studio-save"
          >
            {saving ? <L.Loader2 size={13} className="animate-spin"/> : <L.Save size={13}/>} Save
          </Button>
          <Button
            variant="outline"
            onClick={() => setDubOpen(true)}
            className="btn-pill h-9 text-xs"
            data-testid="studio-dub-open"
            title="Make this piece in another language"
          >
            <L.Languages size={14}/> Translate
          </Button>
          <Button
            variant="ghost"
            onClick={onDuplicate}
            className="btn-pill h-9 text-xs"
            data-testid="studio-duplicate"
            aria-label="Duplicate project"
            title="Duplicate project"
          >
            <L.Copy size={14}/>
          </Button>
          <Button
            variant="ghost"
            onClick={onDelete}
            className="btn-pill h-9 text-xs text-red-500 hover:text-red-600"
            data-testid="studio-delete"
            aria-label="Delete project"
            title="Delete project"
          >
            <L.Trash2 size={14}/>
          </Button>
        </div>
      </div>

      <ScriptTools
        voice={blocks[blocks.length - 1]?.voice || "hale"}
        onBlocks={applyImportedBlocks}
        onTitleSuggestion={(t) => setTitle(cur => (cur && cur !== "Untitled project" ? cur : t))}
        tighten={tighten}
        onTightenChange={(v) => { snapshot(); setTighten(v); }}
        chapterCount={chapterCount}
        onSuggestChapters={suggestChapters}
        suggestingChapters={suggesting}
      />

      {/* Inline hint, written for whatever the user set out to make. */}
      {blocks.length === 1 && !blocks[0].text && (
        <div
          className="rounded-2xl bg-[var(--rammy-cyan)]/8 border border-[var(--rammy-cyan)]/30 p-4 text-sm text-[var(--rammy-text)] leading-relaxed flex items-start gap-3"
          data-testid="studio-empty-hint"
        >
          <L.Info size={16} className="text-[var(--rammy-cyan-dark)] mt-0.5 shrink-0"/>
          <div>
            {mode === "photo" ? (
              <>Add your first photo below and say what should be heard over it. Each
              photo becomes a scene — press <span className="font-medium">Make it move</span> and
              Rammy animates the picture itself, or leave it with a camera move.</>
            ) : mode === "video" ? (
              <>Write your first line below, then give the scene a picture — generate one, or
              use your own. Name a <span className="font-medium">Cast</span> member and the same
              character will show up in every scene.</>
            ) : (
              <>Write your first paragraph below, then press the play icon to hear it in that
              voice before you render anything. Add more paragraphs to switch voices
              mid-script — good for dialogue, interviews and audiobook chapters.</>
            )}
          </div>
        </div>
      )}

      {/* Blocks */}
      <div className="space-y-3" data-testid="studio-blocks">
        {blocks.map((b, i) => (
          <BlockEditor
            key={i}
            index={i}
            block={b}
            voiceOptions={voiceOptions}
            pronunciations={pronunciations}
            canRemove={blocks.length > 1}
            isLast={i === blocks.length - 1}
            willRevoice={plan?.blocks?.[i] ? (!plan.blocks[i].cached && !plan.blocks[i].empty) : false}
            chapterNumber={b.chapter ? blocks.slice(0, i + 1).filter(x => x.chapter).length : 0}
            auditionOpen={auditionIdx === i}
            castList={castList}
            mode={mode}
            onToggleAudition={() => setAuditionIdx(cur => cur === i ? null : i)}
            onChange={(patch) => setBlock(i, patch)}
            onRemove={() => removeBlock(i)}
            onMove={(to) => moveBlock(i, to)}
            onDropAt={(from) => moveBlock(from, i)}
          />
        ))}
        <button
          type="button"
          onClick={addBlock}
          disabled={blocks.length >= MAX_BLOCKS}
          className="w-full rounded-2xl border border-dashed border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] hover:bg-[var(--rammy-cyan)]/5 py-3 text-sm text-[var(--rammy-text-sec)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          data-testid="studio-add-block"
        >
          <L.Plus size={14}/> Add {mode === "photo" ? "photo" : mode === "video" ? "scene" : "paragraph"}
        </button>
      </div>

      {/* Below here is craft, not the main job — so it only appears for the
          modes it belongs to. Showing every panel to everyone is what made
          this screen unreadable. */}
      <PronunciationEditor
        rules={pronunciations}
        onChange={(next) => { snapshot(); setPronunciations(next); }}
      />

      {isAudio && (
        <CoverPicker
          coverDataUrl={coverDataUrl}
          onChange={setCoverDataUrl}
          title={title}
          firstBlockText={blocks[0]?.text || ""}
        />
      )}

      <AmbienceEditor
        ambience={ambience}
        onChange={(next) => { snapshot(); setAmbience(next); }}
      />

      <MusicBed
        music={music}
        onChange={(next) => { snapshot(); setMusic(next); }}
      />

      <ExportOptions
        settings={exportSettings}
        onChange={(next) => { snapshot(); setExportSettings(next); }}
      />

      {brand && <BrandKitPanel brand={brand} onChange={onBrandChange}/>}

      {/* Render bar */}
      <div className="sticky bottom-0 -mx-6 px-6 pt-4 pb-6 bg-gradient-to-t from-[var(--rammy-bg)] via-[var(--rammy-bg)] to-transparent">
        <div className="rounded-3xl bg-[var(--rm-ink-900)] text-white p-5 shadow-[0_18px_44px_rgba(17,19,24,0.24)]">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Render</div>
              <div className="text-sm mt-1 truncate" data-testid="studio-render-status">
                {render
                  ? (isVideoish
                    ? `${fmtClock(render.duration_seconds || 0)} voiced — now export the video`
                    : `${fmtClock(render.duration_seconds || 0)} of audio ready`)
                  : (plan?.to_render === 0 && plan?.total
                    ? "Every line is already voiced — press to stitch it together."
                    : isVideoish
                      ? "Write your scenes, then voice them — the video export comes next."
                      : "Compose your script, then render to hear it.")}
              </div>
              {plan?.credits > 0 && (
                <div className="font-mono text-[11px] text-white/45 mt-1" data-testid="studio-render-cost">
                  uses {plan.credits} credits
                </div>
              )}
            </div>
            <Button
              onClick={generate}
              disabled={generating || overBudget}
              className="btn-pill h-11 text-sm bg-[var(--rammy-cyan)] text-[var(--rammy-text)] hover:bg-[var(--rammy-cyan)]/90 font-semibold"
              data-testid="studio-generate"
            >
              {generating ? <L.Loader2 size={15} className="animate-spin"/> : <L.Sparkles size={15}/>}
              {generating
                ? "Rendering…"
                : (plan && plan.total > 0 && plan.to_render > 0 && plan.to_render < plan.total
                  ? `Re-voice ${plan.to_render} of ${plan.total}`
                  : (render
                    ? "Re-render"
                    : (isVideoish ? "Voice the scenes" : "Render project")))}
            </Button>
          </div>

          {render && (
            <div className="mt-5 space-y-4">
              <AudioTimeline
                audioB64={render.merged_mp3_b64}
                mime="audio/mpeg"
                ambienceB64={render.ambience_mp3_b64}
                ambienceMime={render.ambience_mime || "audio/mpeg"}
                ambienceVolume={render.ambience_volume ?? 0}
                musicUrl={render.music?.url || null}
                musicVolume={render.music?.volume ?? 0}
                musicDuck={render.music?.duck ?? 0.7}
                duckEnvelope={duckEnvelope}
                chapters={render.chapters || []}
                durationHint={render.duration_seconds}
              />

              <div className="flex items-center gap-3 flex-wrap text-[11px]">
                {render.ambience_prompt && (
                  <span className="inline-flex items-center gap-1.5 text-white/70" data-testid="studio-ambience-active">
                    <L.Waves size={13} className="text-[var(--rammy-cyan)]"/>
                    {render.ambience_prompt} · {Math.round((render.ambience_volume || 0) * 100)}%
                  </span>
                )}
                {render.music?.label && (
                  <span className="inline-flex items-center gap-1.5 text-white/70" data-testid="studio-music-active">
                    <L.Music size={13} className="text-[var(--rammy-cyan)]"/>
                    {render.music.label} · {Math.round((render.music.volume || 0) * 100)}% · ducks {Math.round((render.music.duck ?? 0) * 100)}%
                  </span>
                )}
                {render.tightened && (
                  <span className="inline-flex items-center gap-1.5 text-white/70" data-testid="studio-tightened-badge">
                    <L.Scissors size={13} className="text-[var(--rammy-cyan)]"/> Dead air trimmed
                  </span>
                )}
                {savedToLibrary && (
                  <span className="inline-flex items-center gap-1.5 text-[var(--rammy-cyan)]" data-testid="studio-saved-badge">
                    <L.Check size={13}/> Saved to Creations
                  </span>
                )}
              </div>

              {/* Mode toggle */}
              <div className="inline-flex rounded-full bg-white/10 p-1" data-testid="studio-download-toggle">
                <button
                  onClick={() => setDownloadMode("merged")}
                  data-testid="studio-toggle-merged"
                  data-active={downloadMode === "merged" ? "true" : "false"}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    downloadMode === "merged" ? "bg-[var(--rm-surface)] text-[var(--rammy-text)]" : "text-white/70 hover:text-white"
                  }`}
                >
                  Full track
                </button>
                <button
                  onClick={() => setDownloadMode("stems")}
                  data-testid="studio-toggle-stems"
                  data-active={downloadMode === "stems" ? "true" : "false"}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    downloadMode === "stems" ? "bg-[var(--rm-surface)] text-[var(--rammy-text)]" : "text-white/70 hover:text-white"
                  }`}
                >
                  Per-paragraph stems
                </button>
              </div>

              {downloadMode === "merged" ? (
                <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={assembleVideo}
                    disabled={assembling}
                    className="btn-pill h-9 text-xs bg-[var(--rammy-cyan)] text-[var(--rm-ink-900)] hover:bg-[var(--rammy-cyan)]/85 disabled:opacity-60"
                    data-testid="studio-assemble-video"
                    title="Cut your scenes to the voiceover and burn the captions in, server-side"
                  >
                    {assembling
                      ? <><L.Loader2 size={13} className="animate-spin"/>
                          {assembly.status === "voicing" ? "Voicing…" : "Rendering…"}</>
                      : <><L.Clapperboard size={13}/> Make the {exportSettings?.aspect || "9:16"} video</>}
                  </Button>
                  <Button
                    onClick={downloadMerged}
                    className="btn-pill h-9 text-xs bg-[var(--rm-surface)] text-[var(--rammy-text)] hover:bg-[var(--rm-surface)]/90"
                    data-testid="studio-download-merged"
                  >
                    <L.Download size={13}/> Download {(render.music?.url || render.ambience_mp3_b64) ? "mixed audio" : "audio"}
                  </Button>
                  {(render.chapters || []).length > 0 && (
                    <Button
                      onClick={copyTimestamps}
                      variant="ghost"
                      className="btn-pill h-9 text-xs text-white/80 hover:text-white hover:bg-white/10"
                      data-testid="studio-copy-timestamps"
                      title="Copy the 0:00 chapter list for your description"
                    >
                      <L.ListOrdered size={13}/> Copy timestamps
                    </Button>
                  )}
                  <Button
                    onClick={exportAudiogram}
                    disabled={audiogram.busy}
                    className="btn-pill h-9 text-xs bg-[var(--rammy-cyan)]/25 text-white hover:bg-[var(--rammy-cyan)]/40 border border-white/20 disabled:opacity-60"
                    data-testid="studio-export-audiogram"
                    title="The old in-browser export: waveform, Ken Burns stills and captions, no credits"
                  >
                    {audiogram.busy
                      ? (
                        <>
                          <L.Loader2 size={13} className="animate-spin"/>
                          {audiogram.phase === "converting" ? "Converting" : "Rendering"} {Math.round(audiogram.pct * 100)}%
                        </>
                      )
                      : <><L.Film size={13}/> Waveform video</>}
                  </Button>
                </div>

                {assembly?.status === "done" && assembly.url && (
                  <div data-testid="studio-assembly-result">
                    <video
                      src={`${process.env.REACT_APP_BACKEND_URL}${assembly.url}`}
                      controls
                      className="w-full max-h-[420px] rounded-2xl bg-black"
                      data-testid="studio-assembly-video"
                    />
                    <div className="mt-1.5 text-[11px] text-white/55">
                      {assembly.scenes} scene{assembly.scenes === 1 ? "" : "s"}
                      {assembly.with_visuals != null
                        ? ` · ${assembly.with_visuals} with a picture`
                        : ""} · captions burned in · saved to Creations
                    </div>
                  </div>
                )}

                {assembling && (
                  <div className="text-[11px] text-white/60" data-testid="studio-assembly-progress">
                    {assembly.status === "voicing"
                      ? "Voicing the paragraphs that changed…"
                      : "Cutting the scenes to the voice and burning the captions in — this takes a minute."}
                  </div>
                )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[11px] text-white/60">
                    <span>{render.stems?.length || 0} stems ready</span>
                    <button
                      onClick={downloadAllStems}
                      className="text-white/90 hover:text-white uppercase tracking-widest font-semibold"
                      data-testid="studio-download-all-stems"
                    >
                      Download all →
                    </button>
                  </div>
                  <div className="rounded-2xl bg-white/5 divide-y divide-white/10 max-h-56 overflow-y-auto subtle-scroll">
                    {(render.stems || []).map((s, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2" data-testid={`studio-stem-${i}`}>
                        <span className="text-[11px] font-mono text-white/50 w-6">#{i + 1}</span>
                        <span className="text-xs text-white/80 flex-1 truncate">
                          {(blocks[i]?.text || "").slice(0, 60) || "(empty)"}
                        </span>
                        <button
                          onClick={() => playStem(i)}
                          className="text-white/70 hover:text-white p-1"
                          data-testid={`studio-play-stem-${i}`}
                          aria-label={`Play paragraph ${i + 1}`}
                        >
                          <L.Play size={13}/>
                        </button>
                        <button
                          onClick={() => downloadStem(i)}
                          className="text-white/70 hover:text-white p-1"
                          data-testid={`studio-download-stem-${i}`}
                          aria-label={`Download paragraph ${i + 1}`}
                        >
                          <L.Download size={13}/>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Block editor                                                         */
/* ------------------------------------------------------------------ */
function BlockEditor({
  index, block, voiceOptions, pronunciations, canRemove, isLast, willRevoice, chapterNumber,
  auditionOpen, onToggleAudition, onChange, onRemove, onMove, onDropAt, castList = [],
  mode = "audio",
}) {
  const currentLabel = voiceOptions.find(v => v.id === block.voice)?.label
    || (block.voice?.length >= 20 ? "Cloned voice" : block.voice);
  const overCap = (block.text?.length || 0) > MAX_BLOCK_CHARS;
  const [previewing, setPreviewing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const preview = async (voice = block.voice) => {
    const text = (block.text || "").trim();
    if (!text) { toast.error("Write something in this paragraph first"); return; }
    setPreviewing(true);
    try {
      await previewLine({ text: text.slice(0, 600), voice, speed: block.speed, pronunciations, id: `${index}-${voice}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't preview that line");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div
      className={`rounded-2xl bg-[var(--rm-surface)] border p-4 transition-colors ${
        dragOver ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/8" : "border-[var(--rammy-border)]"
      }`}
      data-testid={`studio-block-${index}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", String(index))}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
        if (!Number.isNaN(from)) onDropAt(from);
      }}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
          <L.GripVertical size={13} className="cursor-grab active:cursor-grabbing" data-testid={`studio-block-grip-${index}`}/>
          Paragraph {index + 1}
          {willRevoice ? (
            <span
              className="normal-case tracking-normal text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#E89A3C]/18 text-[#9A6212]"
              data-testid={`studio-block-revoice-${index}`}
              title="This paragraph changed — it will be re-voiced on the next render"
            >
              will re-voice
            </span>
          ) : (block.text?.trim() ? (
            <span
              className="normal-case tracking-normal text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--rammy-cyan)]/18 text-[var(--rammy-cyan-dark)]"
              data-testid={`studio-block-cached-${index}`}
              title="Already voiced — the next render reuses this audio"
            >
              voiced
            </span>
          ) : null)}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => preview()}
            disabled={previewing}
            className="p-1.5 rounded-full text-[var(--rammy-cyan-dark)] hover:bg-[var(--rammy-cyan)]/15 disabled:opacity-50 transition-colors"
            data-testid={`studio-block-preview-${index}`}
            aria-label={`Hear paragraph ${index + 1}`}
            title="Hear this paragraph in this voice"
          >
            {previewing ? <L.Loader2 size={14} className="animate-spin"/> : <L.Play size={14}/>}
          </button>
          <Select value={block.voice} onValueChange={(v) => onChange({ voice: v })}>
            <SelectTrigger
              className="h-8 rounded-full text-xs px-3 border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] w-auto min-w-[130px]"
              data-testid={`studio-voice-select-${index}`}
            >
              <SelectValue placeholder={currentLabel}/>
            </SelectTrigger>
            <SelectContent>
              {voiceOptions.filter(v => v.kind === "named").length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-widest px-2 py-1 text-[var(--rammy-text-muted)]">Rammy voices</div>
                  {voiceOptions.filter(v => v.kind === "named").map(v => (
                    <SelectItem key={v.id} value={v.id} data-testid={`studio-voice-opt-${index}-${v.id}`}>{v.label}</SelectItem>
                  ))}
                </>
              )}
              {voiceOptions.filter(v => v.kind === "cloned").length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-widest px-2 py-1 text-[var(--rammy-text-muted)] mt-1">Your voices</div>
                  {voiceOptions.filter(v => v.kind === "cloned").map(v => (
                    <SelectItem key={v.id} value={v.id} data-testid={`studio-voice-opt-${index}-${v.id}`}>{v.label}</SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          <button
            onClick={() => onChange({ chapter: !block.chapter, chapter_title: block.chapter ? null : (block.chapter_title || "") })}
            data-active={block.chapter ? "true" : "false"}
            className={`p-1.5 rounded-full transition-colors ${
              block.chapter ? "bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]" : "text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)]"
            }`}
            data-testid={`studio-block-chapter-${index}`}
            aria-label={block.chapter ? "Remove chapter mark" : "Start a chapter here"}
            title={block.chapter ? "Remove chapter mark" : "Start a chapter here"}
          >
            {block.chapter ? <L.Bookmark size={14} fill="currentColor"/> : <L.Bookmark size={14}/>}
          </button>
          <button
            onClick={onToggleAudition}
            data-active={auditionOpen ? "true" : "false"}
            className={`p-1.5 rounded-full transition-colors ${
              auditionOpen ? "bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]" : "text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)]"
            }`}
            data-testid={`studio-audition-toggle-${index}`}
            aria-label="Compare voices"
            title="Hear this line in every voice"
          >
            <L.AudioLines size={14}/>
          </button>
          <button
            onClick={() => onMove(index - 1)}
            disabled={index === 0}
            className="p-1 text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] disabled:opacity-30"
            data-testid={`studio-block-up-${index}`}
            aria-label="Move up"
          >
            <L.ArrowUp size={13}/>
          </button>
          <button
            onClick={() => onMove(index + 1)}
            disabled={isLast}
            className="p-1 text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] disabled:opacity-30"
            data-testid={`studio-block-down-${index}`}
            aria-label="Move down"
          >
            <L.ArrowDown size={13}/>
          </button>
          {canRemove && (
            <button
              onClick={onRemove}
              className="p-1 text-[var(--rammy-text-muted)] hover:text-red-500"
              data-testid={`studio-block-remove-${index}`}
              aria-label="Remove paragraph"
            >
              <L.X size={14}/>
            </button>
          )}
        </div>
      </div>

      {block.chapter && (
        <div className="mb-2 flex items-center gap-2" data-testid={`studio-chapter-row-${index}`}>
          <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)] shrink-0">
            Chapter {chapterNumber || 1}
          </span>
          <Input
            value={block.chapter_title || ""}
            onChange={(e) => onChange({ chapter_title: e.target.value })}
            placeholder="Name this section — listeners see it as a jump point"
            maxLength={60}
            className="rounded-full h-8 text-[12px] border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
            data-testid={`studio-chapter-title-${index}`}
          />
        </div>
      )}

      <Textarea
        value={block.text}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder="Write this paragraph…"
        rows={4}
        maxLength={MAX_BLOCK_CHARS + 200}
        className="rounded-xl resize-y min-h-[80px] border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
        data-testid={`studio-block-text-${index}`}
      />

      <SceneVisual
        index={index}
        visual={block.visual}
        castList={castList}
        sceneText={block.text}
        mode={mode}
        onChange={(visual) => onChange({ visual })}
      />

      {auditionOpen && (
        <div className="mt-2 rounded-xl bg-[var(--rammy-cyan)]/8 border border-[var(--rammy-cyan)]/25 p-2.5" data-testid={`studio-audition-${index}`}>
          <div className="text-[10.5px] text-[var(--rammy-text-muted)] mb-1.5">
            Hear this line in each voice, then keep the one you like.
          </div>
          <div className="flex flex-wrap gap-1.5">
            {voiceOptions.map(v => (
              <button
                key={v.id}
                type="button"
                onClick={() => preview(v.id)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border inline-flex items-center gap-1 transition-colors ${
                  v.id === block.voice
                    ? "bg-[var(--rammy-cyan-dark)] text-white border-[var(--rammy-cyan-dark)]"
                    : "bg-[var(--rm-surface)] border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
                }`}
                data-testid={`studio-audition-voice-${index}-${v.id}`}
              >
                <L.Play size={10}/> {v.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onToggleAudition}
            className="mt-2 text-[10.5px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]"
            data-testid={`studio-audition-close-${index}`}
          >
            Done
          </button>
        </div>
      )}

      {/* Pacing controls */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] uppercase tracking-widest text-[var(--rammy-text-muted)] w-14 shrink-0">Speed</span>
          <Slider
            min={75}
            max={125}
            step={5}
            value={[Math.round((block.speed ?? 1) * 100)]}
            onValueChange={([v]) => onChange({ speed: v / 100 })}
            className="flex-1"
            data-testid={`studio-block-speed-${index}`}
          />
          <span className="text-[11px] text-[var(--rammy-text-sec)] w-11 text-right">{(block.speed ?? 1).toFixed(2).replace(/0$/, "")}×</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] uppercase tracking-widest text-[var(--rammy-text-muted)] w-14 shrink-0">Pause</span>
          <Slider
            min={0}
            max={2000}
            step={100}
            value={[block.pause_ms ?? 400]}
            onValueChange={([v]) => onChange({ pause_ms: v })}
            className="flex-1"
            disabled={isLast}
            data-testid={`studio-block-pause-${index}`}
          />
          <span className="text-[11px] text-[var(--rammy-text-sec)] w-11 text-right">
            {isLast ? "—" : `${((block.pause_ms ?? 400) / 1000).toFixed(1)}s`}
          </span>
        </div>
      </div>

      <div className="mt-1.5 flex items-center justify-between text-[11px] text-[var(--rammy-text-muted)]">
        <span>≈ {Math.max(1, Math.round((block.text?.length || 0) / 2.6))}s</span>
        {overCap && <span className="text-red-500">Paragraph is too long</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Cover picker                                                         */
/* ------------------------------------------------------------------ */
function CoverPicker({ coverDataUrl, onChange, title, firstBlockText }) {
  const inputRef = useRef(null);
  const [genBusy, setGenBusy] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const MAX_BYTES = 5 * 1024 * 1024;
  const onPick = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Use a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Cover image must be under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result || "") || null);
    reader.readAsDataURL(file);
  };

  const generateCover = async () => {
    if (genBusy) return;
    const custom = customPrompt.trim();
    const seed = [title, (firstBlockText || "").slice(0, 240)].filter(Boolean).join(" — ").trim();
    if (!custom && !seed) {
      toast.error("Add a title, a first paragraph, or a custom description — Rammy needs a starting point.");
      return;
    }
    setGenBusy(true);
    try {
      const prompt = custom
        ? `Cover artwork for a piece of audio content. Description: ${custom}. ` +
          `Style: cinematic, minimalist editorial cover, warm palette (cream, amber, deep charcoal), ` +
          `soft depth of field, no text or typography anywhere in the image, square 1:1 composition.`
        : `Cover artwork for an audio piece titled "${title || "Untitled"}". ` +
          `Themes drawn from: ${(firstBlockText || "").slice(0, 400)}. ` +
          `Style: cinematic, minimalist editorial cover, warm palette (cream, amber, deep charcoal), ` +
          `soft depth of field, no text or typography anywhere in the image, square 1:1 composition ` +
          `with breathing room at the top for a title overlay.`;
      const { data } = await api.post("/create/image", { prompt, size: "1024x1024" });
      onChange(`data:${data.mime};base64,${data.image_base64}`);
      toast.success(data.fallback_reason ? "Cover ready (generated via Flux)" : "Cover ready");
    } catch (e) {
      console.warn("cover generation failed", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't generate a cover — try again in a moment.");
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-[var(--rammy-cyan)]/5 border border-[var(--rammy-cyan)]/30 p-4" data-testid="studio-cover">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-[var(--rammy-text)]">Cover image</div>
          <div className="text-[11px] text-[var(--rammy-text-muted)]">
            Optional — shown behind the waveform in the exported video.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {coverDataUrl && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] hover:text-red-600 transition-colors"
              data-testid="studio-cover-remove"
            >
              Remove
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => onPick(e.target.files)}
            data-testid="studio-cover-input"
          />
          <button
            type="button"
            onClick={generateCover}
            disabled={genBusy}
            className="btn-pill h-8 text-[11px] px-3 bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] border border-[var(--rammy-cyan)]/40 hover:bg-[var(--rammy-cyan)]/30 disabled:opacity-60 inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid="studio-cover-generate"
            title="Let Rammy draft a cover from your title and first paragraph"
          >
            {genBusy
              ? <><L.Loader2 size={12} className="animate-spin"/> Generating…</>
              : <><L.Sparkles size={12}/> Generate with Rammy</>}
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="btn-pill h-8 text-[11px] px-3 bg-[var(--rm-surface)] text-[var(--rammy-text)] border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan-dark)] inline-flex items-center gap-1.5"
            data-testid="studio-cover-pick"
          >
            <L.ImagePlus size={12}/> {coverDataUrl ? "Replace" : "Upload"}
          </button>
        </div>
      </div>
      {coverDataUrl && (
        <div className="mt-3">
          <img
            src={coverDataUrl}
            alt="Cover preview"
            className="w-24 h-24 rounded-xl object-cover border border-[var(--rammy-border)]"
            data-testid="studio-cover-preview"
          />
        </div>
      )}
      <div className="mt-3">
        <div className="relative">
          <L.Sparkles size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rammy-cyan-dark)] pointer-events-none"/>
          <input
            type="text"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); generateCover(); } }}
            maxLength={200}
            placeholder="Or describe the cover you want (e.g. terracotta desert at dusk)"
            className="w-full h-9 pl-8 pr-3 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-border)] focus:border-[var(--rammy-cyan-dark)] focus:outline-none text-[12px] placeholder:text-[var(--rammy-text-muted)]/80"
            data-testid="studio-cover-custom-prompt"
          />
        </div>
      </div>
    </div>
  );
}


